import React, { useState } from 'react';
import {
  FastForward, Hide, Link as LinkIcon, Sync,
} from '@styled-icons/boxicons-regular';
import { DreamBlock } from '@typedream/core';
import { isBlock } from '@udecode/plate-core';
import styled from 'styled-components';
import {
  getURL,
  hasProperty,
  isLinkableElement,
} from '../../../editorTools/queries';
import { ContainerElementPluginOptions } from '../../../nodes/container/types';
import { getTPlugin, useTPlateEditorRef } from '../../../plate';
import { ELEMENT_CONTAINER } from '../../../pluginKeys';

const Container = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  z-index: 1;
  position: absolute;
  right: 0;
  bottom: 0;

  & svg {
    color: var(--text-on-accent);
    background: var(--accent);
    border: 1px solid var(--border-modal);
    border-radius: 50%;
    padding: 2px;
    box-shadow: var(--shadow-modal);
    cursor: pointer;
    user-select: none;
    margin-left: 5px;
  }
`;

const StyledSyncBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  user-select: none;
`;

const StyledSyncBadgeName = styled.div`
  padding: 1px 8px;
  border-radius: 3px;
  background-color: var(--background-button);
  border: 1px solid var(--border-modal);
  box-shadow: var(--shadow-modal);
  color: var(--text-lighter) !important;
  margin-right: -10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 80px;
  transition: all 0.2s;
  direction: rtl;
  text-align: left;
  font-size: 0.75rem;

  &:hover {
    width: fit-content;
    max-width: 700px;
  }
`;

interface BadgesProps {
  element: DreamBlock
}

function SyncBadge(props: BadgesProps) {
  const editor = useTPlateEditorRef();
  const { element } = props;

  if (!hasProperty(editor, element, 'transclusion_reference_id')) {
    return null;
  }

  const transRefID = element.transclusion_reference_id;
  const referenceName = element.name;
  if (!transRefID || !element.transclusion_enabled) { return null; }

  // get the transclusion reference name
  const { options } = getTPlugin<ContainerElementPluginOptions>(
    editor,
    ELEMENT_CONTAINER,
  );
  if (!options) { return null; }

  const { onEditSyncedReference, isTransclusionEnabled } = options;

  if (!isTransclusionEnabled) return null;

  return (
    <StyledSyncBadgeContainer contentEditable={false}>
      <StyledSyncBadgeName
        role="none"
        onClick={() => {
          // Handle redirect to form editor, implemented on the app level
          onEditSyncedReference(transRefID);
        }}
      >
        {referenceName}
      </StyledSyncBadgeName>
      <Sync size={22} />
    </StyledSyncBadgeContainer>
  );
}

export function Badges(props: BadgesProps) {
  const editor = useTPlateEditorRef();
  const { element } = props;

  const badges = {
    link:
      isBlock(editor, element)
      && isLinkableElement(element)
      && !!getURL(element),
    hiddenOnDesktop:
      hasProperty(editor, element, 'desktop_hide') && !!element.desktop_hide,
    animation: hasProperty(editor, element, 'animation') && !!element.animation,
    sync:
      isBlock(editor, element)
      && hasProperty(editor, element, 'transclusion_enabled')
      && !!element.transclusion_enabled && !!element.transclusion_reference_id,
  };

  if (Object.values(badges).filter(Boolean).length === 0) {
    return null;
  }

  return (
    <Container>
      {badges.sync && <SyncBadge {...props} />}
      {badges.link && <LinkIcon size={22} />}
      {badges.hiddenOnDesktop && <Hide size={22} />}
      {badges.animation && <FastForward size={22} />}
    </Container>
  );
}
